import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSettings } from '../context/SettingsContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { fetchHeroSliders, HeroSlider } from '../api/heroSliders';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const Hero: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { getSetting } = useSettings();
    const isRTL = i18n.dir() === 'rtl';

    const [sliders, setSliders] = useState<HeroSlider[]>([]);
    const [isLoadingSliders, setIsLoadingSliders] = useState(true);

    const heroTitle = i18n.language === 'ar' ? getSetting('hero_title_ar', t('home.heroTitle')) : getSetting('hero_title_fr', t('home.heroTitle'));
    const heroSubtitle = i18n.language === 'ar' ? getSetting('hero_subtitle_ar', t('home.heroSubtitle')) : getSetting('hero_subtitle_fr', t('home.heroSubtitle'));
    const heroBadge = i18n.language === 'ar' ? getSetting('hero_badge_ar', t('home.heroBadge')) : getSetting('hero_badge_fr', t('home.heroBadge'));
    const fallbackImage = getSetting('hero_image', 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80');

    useEffect(() => {
        const loadSliders = async () => {
            try {
                const data = await fetchHeroSliders();
                setSliders(data);
            } catch (error) {
                console.error('Failed to load hero sliders:', error);
            } finally {
                setIsLoadingSliders(false);
            }
        };
        loadSliders();
    }, []);

    return (
        <div className="bg-slate-50 py-6 md:py-10">
            <div className="container mx-auto px-4">
                <div className="relative h-[50vh] md:h-[70vh] lg:h-[80vh] w-full overflow-hidden rounded-3xl shadow-2xl group border border-white/20">
                    {/* Full-width Background Slider */}
                    {sliders.length > 0 ? (
                        <Swiper
                            modules={[Autoplay, EffectFade, Navigation, Pagination]}
                            effect="fade"
                            speed={1000}
                            autoplay={{ delay: 5000, disableOnInteraction: false }}
                            loop={true}
                            navigation={{
                                nextEl: '.swiper-button-next-custom',
                                prevEl: '.swiper-button-prev-custom',
                            }}
                            pagination={{
                                clickable: true,
                                dynamicBullets: true,
                            }}
                            className="w-full h-full"
                        >
                            {sliders.map((slide) => (
                                <SwiperSlide key={slide.id}>
                                    {slide.link ? (
                                        <Link to={slide.link} className="block w-full h-full cursor-pointer">
                                            <img
                                                src={slide.image}
                                                alt={slide.alt || 'Promotion'}
                                                className="w-full h-full object-cover"
                                            />
                                        </Link>
                                    ) : (
                                        <div className="w-full h-full">
                                            <img
                                                src={slide.image}
                                                alt={slide.alt || 'Promotion'}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    )}
                                </SwiperSlide>
                            ))}

                            {/* Custom Navigation Arrows */}
                            <button className="swiper-button-prev-custom absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 md:w-14 h-11 md:h-14 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-md border border-white/20 text-white transition-all duration-300 hover:bg-black/50 hover:scale-110 shadow-lg">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button className="swiper-button-next-custom absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 md:w-14 h-11 md:h-14 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-md border border-white/20 text-white transition-all duration-300 hover:bg-black/50 hover:scale-110 shadow-lg">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </Swiper>
                    ) : (
                        <div className="w-full h-full relative">
                            <img
                                src={fallbackImage}
                                alt="Hero"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-neutral-900/10 backdrop-blur-[1px]"></div>
                        </div>
                    )}
                </div>
            </div>

            <style aria-hidden="true">{`
                .swiper-pagination-bullet {
                    background: white !important;
                    opacity: 0.5;
                    width: 10px;
                    height: 10px;
                    transition: all 0.3s ease;
                }
                .swiper-pagination-bullet-active {
                    opacity: 1;
                    width: 24px;
                    border-radius: 5px;
                    background: white !important;
                }
            `}</style>
        </div>
    );
};

export default Hero;
